import type { AdminFileFolder } from '@mds/shared/types/file';

/**
 * The folder tree, rebuilt from the flat list the API returns.
 *
 * Folders are few — dozens, not thousands — so the Files page loads them all
 * once and answers breadcrumbs, subfolder rows and destination pickers from
 * memory instead of asking the API each time.
 */
export interface FolderTree {
  byId: Map<string, AdminFileFolder>;
  /** Each parent's folders in name order. The key `null` is the root. */
  children: Map<string | null, AdminFileFolder[]>;
}

/** What the root of the library is called wherever a folder name would go. */
export const ROOT_LABEL = 'Files';

export function buildTree(folders: readonly AdminFileFolder[]): FolderTree {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const children = new Map<string | null, AdminFileFolder[]>();
  for (const folder of folders) {
    // A parent missing from the list would strand its subtree; show it at the root instead.
    const parent = folder.parentId && byId.has(folder.parentId) ? folder.parentId : null;
    const siblings = children.get(parent) ?? [];
    siblings.push(folder);
    children.set(parent, siblings);
  }
  return { byId, children };
}

/** From the top down to the folder itself. Empty for the root or a folder not in the tree. */
export function pathTo(tree: FolderTree, folderId: string | null): AdminFileFolder[] {
  const path: AdminFileFolder[] = [];
  const seen = new Set<string>();
  for (let at = folderId; at !== null && !seen.has(at);) {
    const folder = tree.byId.get(at);
    if (!folder) break;
    seen.add(at);
    path.unshift(folder);
    at = folder.parentId;
  }
  return path;
}

/** "Marketing / 2026", or the root's name for the root. */
export function pathLabel(tree: FolderTree, folderId: string | null): string {
  const path = pathTo(tree, folderId);
  return path.length > 0 ? path.map((folder) => folder.name).join(' / ') : ROOT_LABEL;
}

/** The folder and everything inside it, at any depth — where it cannot be moved to. */
export function subtreeIds(tree: FolderTree, folderId: string): Set<string> {
  const ids = new Set<string>();
  const stack = [folderId];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (ids.has(id)) continue;
    ids.add(id);
    for (const child of tree.children.get(id) ?? []) stack.push(child.id);
  }
  return ids;
}

/** Every folder in tree order (each followed by its own subfolders), minus `exclude`. */
export function inTreeOrder(
  tree: FolderTree,
  exclude: ReadonlySet<string> = new Set(),
): AdminFileFolder[] {
  const ordered: AdminFileFolder[] = [];
  const visit = (parent: string | null) => {
    for (const folder of tree.children.get(parent) ?? []) {
      if (exclude.has(folder.id)) continue;
      ordered.push(folder);
      visit(folder.id);
    }
  };
  visit(null);
  return ordered;
}

/** The value a destination picker uses for the root, since an option value is a string. */
export const ROOT_VALUE = 'root';

/** Options for a destination picker: the root, then every folder by its full path. */
export function destinationOptions(tree: FolderTree, exclude?: ReadonlySet<string>) {
  return [
    { value: ROOT_VALUE, label: `${ROOT_LABEL} (top level)` },
    ...inTreeOrder(tree, exclude).map((folder) => ({
      value: folder.id,
      label: pathLabel(tree, folder.id),
    })),
  ];
}

export const toFolderId = (value: string): string | null => (value === ROOT_VALUE ? null : value);
export const toFolderValue = (folderId: string | null): string => folderId ?? ROOT_VALUE;
